/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *---------------------------------------------------
 */
//Standard libraries
import { call, put, select } from "redux-saga/effects";
import { AnyAction } from "redux";
//Foundation libraries
import cartService from "../../../_foundation/apis/transaction/cart.service";
import shippingInfoService from "../../../_foundation/apis/transaction/shippingInfo.service";
//Custom libraries
import { ORDER_CONFIGS, MINICART_CONFIGS } from "../../../configs/order";
import {
  EMPTY_STRING,
  SELLER_PARAM,
  SUCCESS_MSG_PREFIX,
} from "../../../constants/common";
//Redux
import * as ACTIONS from "../../action-types/order";
import { HANDLE_SUCCESS_MESSAGE_ACTION } from "../../actions/success";
import { CART } from "../../../constants/routes";
import {
  COPY_CART_SUCCESS_ACTION,
  COPY_CART_ERROR_ACTION,
  FETCH_ORDERS_SUCCESS_ACTION,
  FETCH_ORDERS_ERROR_ACTION,
  FETCH_ACTIVE_INPROGRESS_ORDER_ITEM_SUCCESS_ACTION,
  FETCH_ACTIVE_INPROGRESS_ORDER_ITEM_ERROR_ACTION,
  FETCH_ACTIVE_INPROGRESS_ORDER_ITEM_ACTION,
  COMBINE_INPROGRESS_ORDERS_ACTION,
} from "../../actions/order";
import { fetchOrderItemDetailsByIds } from "./orderDetails";
import orderService from "../../../_foundation/apis/transaction/order.service";
import {
  activeInprogressOrderSelector,
} from "../../selectors/order";
import { getSite } from "../../../_foundation/hooks/useSite";
import { N_ITEMS_ADDED, PARTIAL_COPY_ORDER } from "../../../constants/order";
import { VALIDATION_ERROR_ACTION } from "../../actions/error";

export function* copyCart(action: AnyAction) {
  try {
    const { payload } = action;
    let beforeCopyCartQuantity: number = 0;
    let afterCopyCartQuantity: number = 0;
    try {
      const beforeCopyParameters = {
        ...payload,
        sortOrderItemBy: ORDER_CONFIGS.sortOrderItemBy,
      };
      const beforeCopyResponseCart = yield call(cartService.getCart, {
        ...beforeCopyParameters,
      });
      beforeCopyCartQuantity =
        beforeCopyResponseCart?.data?.orderItem
          ?.flatMap((order) => parseInt(order.quantity))
          .reduce((previous, current) => previous + current, 0) ?? 0;
    } catch (e) {
      console.log("error in getting cart", e);
      beforeCopyCartQuantity = 0;
    }
    const activeInprogressOrder = yield select(activeInprogressOrderSelector);
    const {
      fromOrderId: fromOrderId_1,
      widget,
      orderItemCount,
      errorMessage,
    } = payload;
    const params: any = {
      body: {
        fromOrderId_1,
        toOrderId: activeInprogressOrder
          ? activeInprogressOrder.orderId
          : ".**.",
        copyOrderItemId_1: "*",
      },
    };
    if (widget) {
      params["widget"] = widget;
    }
    let response = yield call(cartService.copyOrder, params);
    try {
      const afterCopyParameters = {
        ...payload,
        sortOrderItemBy: ORDER_CONFIGS.sortOrderItemBy,
      };

      const afterCopyResponseCart = yield call(cartService.getCart, {
        ...afterCopyParameters,
      });
      afterCopyCartQuantity =
        afterCopyResponseCart?.data?.orderItem
          ?.flatMap((order) => parseInt(order.quantity))
          .reduce((previous, current) => previous + current, 0) ?? 0;
    } catch (e) {
      console.log("error in getting cart", e);
      afterCopyCartQuantity = 0;
    }
    if (afterCopyCartQuantity > (beforeCopyCartQuantity ?? 0)) {
      // also need to call calculate -- since the copy_order API doesn't
      params.body["calculationUsageId"] =
        ORDER_CONFIGS.calculationUsage.split(",");
      response = yield call(cartService.calculateOrder, params);

      yield put(COPY_CART_SUCCESS_ACTION({ response }));

      const b2b = getSite()?.isB2B;
      const link = !b2b
        ? { url: CART, textKey: `${SUCCESS_MSG_PREFIX}ViewCart` }
        : undefined;
      const successMessage =
        afterCopyCartQuantity === beforeCopyCartQuantity + orderItemCount
          ? {
              key: SUCCESS_MSG_PREFIX + COPY_CART_SUCCESS_ACTION.type,
              link,
            }
          : { key: SUCCESS_MSG_PREFIX + PARTIAL_COPY_ORDER, link };
      yield put(HANDLE_SUCCESS_MESSAGE_ACTION(successMessage));
    } else {
      yield put(VALIDATION_ERROR_ACTION({ errorMessage }));
    }
  } catch (e) {
    yield put(COPY_CART_ERROR_ACTION(e));
  }
}

/**
 * Saga worker to invoke add item API
 */
export function* addItem(action: any) {
  try {
    const payload = action.payload;
    const cartPayload = {
      contractId: payload.contractId,
    };

    const _orderItems: any[] = [];
    let catentryIds: string[] = [];
    let partnumbers: string[] = [];
    let quantities: any[] = [];
    let physicalStoreIds: string[] = [];
    let shipModeIds: string[] = [];
    if (payload.partnumber) {
      partnumbers =
        payload.partnumber instanceof Array
          ? payload.partnumber
          : [payload.partnumber];
      quantities =
        payload.quantity instanceof Array
          ? payload.quantity
          : [payload.quantity];
    } else if (payload.catentryId) {
      catentryIds =
        payload.catentryId instanceof Array
          ? payload.catentryId
          : [payload.catentryId];
      quantities =
        payload.quantity instanceof Array
          ? payload.quantity
          : [payload.quantity];
    }
    if (payload.physicalStoreId) {
      physicalStoreIds =
        payload.physicalStoreId instanceof Array
          ? payload.physicalStoreId
          : [payload.physicalStoreId];
    }
    if (payload.shipModeId) {
      shipModeIds =
        payload.shipModeId instanceof Array
          ? payload.shipModeId
          : [payload.shipModeId];
    }

    for (const i in partnumbers) {
      _orderItems[i] = {
        quantity: quantities[i].toString(),
        partNumber: partnumbers[i],
        contractId: payload.contractId,
      };
      if (physicalStoreIds[i]) {
        Object.assign(_orderItems[i], { physicalStoreId: physicalStoreIds[i] });
      }
      if (shipModeIds[i]) {
        Object.assign(_orderItems[i], { shipModeId: shipModeIds[i] });
      }
    }
    for (const i in catentryIds) {
      _orderItems[i] = {
        quantity: quantities[i].toString(),
        productId: catentryIds[i],
        contractId: payload.contractId,
      };
      if (physicalStoreIds[i]) {
        Object.assign(_orderItems[i], { physicalStoreId: physicalStoreIds[i] });
      }
      if (shipModeIds[i]) {
        Object.assign(_orderItems[i], { shipModeId: shipModeIds[i] });
      }
    }
    let body = {};
    if (payload.widget) {
      body["widget"] = payload.widget;
      cartPayload["widget"] = payload.widget;
    }
    const activeInprogressOrder = yield select(activeInprogressOrderSelector);
    let response: any = {};
    if (!activeInprogressOrder) {
      body = {
        body: {
          orderId: ".",
          x_calculateOrder: ORDER_CONFIGS.calculateOrder,
          orderItem: _orderItems,
          x_calculationUsage: ORDER_CONFIGS.calculationUsage,
          x_inventoryValidation: ORDER_CONFIGS.inventoryValidation,
        },
      };
      response = yield call(cartService.addOrderItem, body);
    } else {
      const orderItems = {};
      for (const i in partnumbers) {
        Object.assign(orderItems, {
          [`quantity_${i}`]: quantities[i].toString(),
          [`partNumber_${i}`]: partnumbers[i],
          [`contractId_${i}`]: payload.contractId,
        });
      }
      for (const i in catentryIds) {
        Object.assign(orderItems, {
          [`quantity_${i}`]: quantities[i].toString(),
          [`catEntryId_${i}`]: catentryIds[i],
          [`contractId_${i}`]: payload.contractId,
        });
      }
      Object.assign(orderItems, {
        orderId: activeInprogressOrder.orderId,
        calculateOrder: ORDER_CONFIGS.calculateOrder,
        inventoryValidation: ORDER_CONFIGS.inventoryValidation,
      });
      body = {
        body: orderItems,
        ...body,
      };
      response = yield call(cartService.addPreConfigurationOrderItem, body);
    }

    yield put({
      type: ACTIONS.ITEM_ADD_SUCCESS,
      response: response.data,
      payload: payload,
    });

    const cartAction = { ...action, payload: cartPayload };
    yield call(fetchCart, cartAction);

    const b2b = getSite()?.isB2B;
    const link =
      !b2b && partnumbers.length === 1
        ? { url: CART, textKey: `${SUCCESS_MSG_PREFIX}ViewCart` }
        : undefined;
    const msg = {
      key:
        SUCCESS_MSG_PREFIX +
        (partnumbers.length > 1 ? N_ITEMS_ADDED : ACTIONS.ITEM_ADD_SUCCESS),
      messageParameters:
        partnumbers.length > 1 ? { v: String(partnumbers.length) } : undefined,
      link,
    };
    yield put(HANDLE_SUCCESS_MESSAGE_ACTION(msg));
  } catch (error) {
    yield put({ type: ACTIONS.ITEM_ADD_ERROR, error });
  }
}

/**
 * Saga worker to invoke remove item API
 */
export function* removeItem(action: any) {
  try {
    const payload = action.payload;
    const { widget, orderItemId } = payload;
    const activeInprogressOrder = yield select(activeInprogressOrderSelector);
    const orderId = activeInprogressOrder
      ? activeInprogressOrder.orderId
      : undefined;
    const body = {
      orderId,
      x_calculateOrder: ORDER_CONFIGS.calculateOrder,
      x_calculationUsage: ORDER_CONFIGS.calculationUsage,
      orderItemId,
      x_inventoryValidation: ORDER_CONFIGS.inventoryValidation,
      orderItem: [
        {
          quantity: "0",
          orderItemId: orderItemId,
        },
      ],
    };

    const response = yield call(cartService.updateOrderItem, { body, widget });

    yield put({ type: ACTIONS.ITEM_REMOVE_SUCCESS, response, payload });
  } catch (error) {
    yield put({ type: ACTIONS.ITEM_REMOVE_ERROR, error });
  }
}

export function* removeItems(action: any) {
  try {
    /*console.log("action => ", action)*/
    const payload = action.payload;
    const orderId = payload.orderId;
    const orderItemId = payload.orderItemId;
    const skipSnackbar = payload.skipSnackbar || false;
    const body = {
      body: {
        orderId: orderId,
        orderItemId: orderItemId,
        calculateOrder: ORDER_CONFIGS.calculateOrder,
        calculationUsage: "-1,-2,-5,-6,-7",
        /*x_calculationUsage: ORDER_CONFIGS.calculationUsage,*/
      },
    };
    if (payload.widget) {
      body["widget"] = payload.widget;
    }

    const successMessage = {
      key: SUCCESS_MSG_PREFIX + ACTIONS.ITEMS_REMOVE_SUCCESS,
    };

    const response = yield call(cartService.deleteOrderItem, body);
    yield put({ type: ACTIONS.ITEMS_REMOVE_SUCCESS, response, payload });
    if (skipSnackbar === false) {
      yield put(HANDLE_SUCCESS_MESSAGE_ACTION(successMessage));
    }
  } catch (error) {
    yield put({ type: ACTIONS.ITEM_REMOVE_ERROR, error });
  }
}
/**
 * Saga worker to invoke update item API
 */
export function* updateItem(action: any) {
  try {
    const payload = action.payload;
    const { orderItemId, quantity, widget } = payload;
    const activeInprogressOrder = yield select(activeInprogressOrderSelector);
    const orderId = activeInprogressOrder
      ? activeInprogressOrder.orderId
      : undefined;
    const body = {
      orderId,
      x_calculateOrder: ORDER_CONFIGS.calculateOrder,
      x_calculationUsage: ORDER_CONFIGS.calculationUsage,
      x_inventoryValidation: ORDER_CONFIGS.inventoryValidation,
      orderItem: [
        {
          quantity: quantity,
          orderItemId: orderItemId,
        },
      ],
    };

    const response = yield call(cartService.updateOrderItem, { widget, body });

    yield put({ type: ACTIONS.ITEM_UPDATE_SUCCESS, response, payload });
  } catch (error) {
    yield put({ type: ACTIONS.ITEM_UPDATE_ERROR, error });
  }
}

export function* initFromStorageFetchCart(action: any) {
  const { WCToken } = action.payload || {};
  if (!!WCToken) {
    yield* fetchCart(action);
  }
}

/**
 * Saga worker to invoke get cart API
 */
export function* fetchCart(action: any) {
  const activeInprogressOrder = yield select(activeInprogressOrderSelector);
  try {
    const payload = action.payload;
    const parameters = {
      ...payload,
      sortOrderItemBy: ORDER_CONFIGS.sortOrderItemBy,
    };

    const fetchCatentries: boolean = payload.fetchCatentries
      ? payload.fetchCatentries
      : false;
    const checkInventory: boolean = payload.checkInventory
      ? payload.checkInventory
      : false;

    if (payload.widget) {
      parameters["widget"] = payload.widget;
    }

    if (!activeInprogressOrder) {
      const responseCart = yield call(cartService.getCart, { ...parameters });
      yield* fetchShipInfo(action);

      let catentries: any = null;
      if (responseCart) {
        const cart = responseCart.data;

        if (cart) {
          const orderItems = cart.orderItem;

          if (orderItems && orderItems.length > 0) {
            let catentryIdList: string[] = [];
            const contracts = orderItems.reduce((p, c) => {
              if (p[c.contractId]) {
                if (p[c.contractId].indexOf(c.productId) === -1) {
                  p[c.contractId].push(c.productId);
                }
              } else {
                p[c.contractId] = [c.productId];
              }
              return p;
            }, {});

            if (
              fetchCatentries ||
              orderItems.length <= MINICART_CONFIGS.maxItemsToShow
            ) {
              //get product info for all items
              orderItems.forEach((item: any, index: number) => {
                catentryIdList.push(item.productId);
              });
            } else {
              //get product info for mini cart only
              orderItems
                .slice(MINICART_CONFIGS.maxItemsToShow * -1)
                .forEach((item: any, index: number) => {
                  catentryIdList.push(item.productId);
                });
            }

            if (catentryIdList.length > 0) {
              catentryIdList = [...new Set(catentryIdList)];

              const currency = parameters ? parameters.currency : "";
              const paramsProduct: any = {
                currency: currency,
                id: catentryIdList,
                contracts: contracts,
                // add empty seller parameter -- don't filter when fetching order-item details
                query: { [SELLER_PARAM]: EMPTY_STRING },
              };
              if (parameters?.cancelToken) {
                paramsProduct["cancelToken"] = parameters.cancelToken;
              }
              if (parameters?.widget) {
                paramsProduct["widget"] = parameters.widget;
              }

              try {
                const contents = yield call(
                  fetchOrderItemDetailsByIds,
                  paramsProduct
                );

                if (contents) {
                  catentries = {};
                  contents.forEach((catentry: any, index: number) => {
                    const { seller, sellerId } = catentry;
                    const obj = {
                      name: catentry.name,
                      thumbnail: catentry.thumbnail,
                      attributes: catentry.attributes,
                      seo: catentry.seo,
                      disallowRecurringOrder: catentry.disallowRecurringOrder,
                      parentCatalogGroupID: catentry.parentCatalogGroupID,
                      seller,
                      sellerId,
                    };
                    catentries[catentry.id] = obj;
                  });
                }
              } catch (error) {
                console.log("Could not retrieve products");
                //Cannot retrieve catentry details; return order items as-is
                catentries = null;
              }
            }
          }
        }
      }
      if (catentries != null) {
        yield put({
          type: ACTIONS.CART_GET_SUCCESS,
          response: responseCart.data,
          catentries: catentries,
          checkInventory: checkInventory,
        });
      } else {
        yield put({
          type: ACTIONS.CART_GET_SUCCESS,
          response: responseCart.data,
          checkInventory: checkInventory,
        });
      }
    } else {
      const siteInfo = getSite();
      const oderPayload = {
        contractId: payload.contractId,
        orderId: activeInprogressOrder.orderId,
        skipErrorSnackbar: true,
        currency: siteInfo?.defaultCurrencyID ?? "",
      };
      yield put(FETCH_ACTIVE_INPROGRESS_ORDER_ITEM_ACTION(oderPayload));
    }
  } catch (error) {
    yield put({
      type: ACTIONS.CART_GET_ERROR,
      error,
    });
  }
}

/**
 * Saga worker to invoke get usable ship info API
 */
export function* fetchShipInfo(action: any) {
  try {
    const payload = action.payload;
    const response = yield call(cartService.getUsableShippingInfo, payload);
    yield put({
      type: ACTIONS.SHIPINFO_GET_SUCCESS,
      response: response.data,
    });
  } catch (error) {
    yield put({ type: ACTIONS.SHIPINFO_GET_ERROR, error });
  }
}

/**
 * Saga worker to invoke get usable ship modes API
 */
export function* fetchShipModes(action: any) {
  try {
    const payload = action.payload;
    const response = yield call(cartService.getUsableShippingMode, payload);
    yield put({
      type: ACTIONS.SHIPMODES_GET_SUCCESS,
      response: response.data,
    });
  } catch (error) {
    yield put({ type: ACTIONS.SHIPMODES_GET_ERROR, error });
  }
}

/**
 * Saga worker to invoke update ship mode API
 */
export function* updateShipMode(action: any) {
  const payload = action.payload;
  const body = {
    body: {
      x_calculateOrder: ORDER_CONFIGS.calculateOrder,
      x_calculationUsage: ORDER_CONFIGS.calculationUsage,
      x_allocate: ORDER_CONFIGS.allocate,
      x_backorder: ORDER_CONFIGS.backOrder,
      x_remerge: ORDER_CONFIGS.remerge,
      x_check: ORDER_CONFIGS.check,
      orderId: ".",
      shipModeId: payload.shipModeId,
      //addressId: payload.shipAddressId,
      orderItem: [], //bypass defect HC-2784
    },
  };
  if (payload.widget) {
    body["widget"] = payload.widget;
  }
  const response = yield call(
    shippingInfoService.updateOrderShippingInfo,
    body
  );
  yield put({
    type: ACTIONS.SHIPMODE_UPDATE_SUCCESS,
    response: response.data,
  });
}

/**
 * Saga worker to invoke get usable payment methods API
 */
export function* fetchPayMethods(action: any) {
  try {
    const payload = action.payload;
    const response = yield call(cartService.getUsablePaymentInfo, payload);
    yield put({
      type: ACTIONS.PAYMETHODS_GET_SUCCESS,
      response: response.data,
    });
  } catch (error) {
    yield put({ type: ACTIONS.PAYMETHODS_GET_ERROR, error });
  }
}

export function* getAllOrders(action: any) {
  try {
    const payload = action.payload;
    const response = yield call(orderService.findByStatus, payload);
    yield put({
      type: FETCH_ORDERS_SUCCESS_ACTION.type,
      response: response.data,
    });
  } catch (error) {
    yield put({ type: FETCH_ORDERS_ERROR_ACTION.type, error });
  }
}

export function* fetchActiveInprogressOrderItem(action: any) {
  try {
    const payload = action.payload;
    const response = yield call(orderService.findByOrderId, payload);
    const orderDetails = response.data;

    const fetchCatentries: boolean = payload.fetchCatentries
      ? payload.fetchCatentries
      : false;

    const checkInventory: boolean = payload.checkInventory
      ? payload.checkInventory
      : false;

    let catentries: any = null;
    if (orderDetails) {
      const orderItems = orderDetails.orderItem;
      if (orderItems && orderItems.length > 0) {
        let catentryIdList: string[] = [];
        const contracts = orderItems.reduce((p, c) => {
          if (p[c.contractId]) {
            if (p[c.contractId].indexOf(c.productId) === -1) {
              p[c.contractId].push(c.productId);
            }
          } else {
            p[c.contractId] = [c.productId];
          }
          return p;
        }, {});

        if (
          fetchCatentries ||
          orderItems.length <= MINICART_CONFIGS.maxItemsToShow
        ) {
          //get product info for all items
          orderItems.forEach((item: any, index: number) => {
            catentryIdList.push(item.productId);
          });
        } else {
          //get product info for mini cart only
          orderItems
            .slice(MINICART_CONFIGS.maxItemsToShow * -1)
            .forEach((item: any, index: number) => {
              catentryIdList.push(item.productId);
            });
        }

        if (catentryIdList.length > 0) {
          catentryIdList = [...new Set(catentryIdList)];

          const currency = payload ? payload.currency : "";
          const paramsProduct: any = {
            currency: currency,
            id: catentryIdList,
            contracts: contracts,
            // add empty seller parameter -- don't filter when fetching order-item details
            query: { [SELLER_PARAM]: EMPTY_STRING },
          };
          if (payload?.cancelToken) {
            paramsProduct["cancelToken"] = payload.cancelToken;
          }
          if (payload?.widget) {
            paramsProduct["widget"] = payload.widget;
          }

          try {
            const contents = yield call(
              fetchOrderItemDetailsByIds,
              paramsProduct
            );

            if (contents) {
              catentries = {};
              contents.forEach((catentry: any, index: number) => {
                const { seller, sellerId } = catentry;
                const obj = {
                  name: catentry.name,
                  thumbnail: catentry.thumbnail,
                  attributes: catentry.attributes,
                  seo: catentry.seo,
                  disallowRecurringOrder: catentry.disallowRecurringOrder,
                  parentCatalogGroupID: catentry.parentCatalogGroupID,
                  seller,
                  sellerId,
                };
                catentries[catentry.id] = obj;
              });
            }
          } catch (error) {
            console.log("Could not retrieve products");
            //Cannot retrieve catentry details; return order items as-is
            catentries = null;
          }
        }
      }
    }

    yield put(
      FETCH_ACTIVE_INPROGRESS_ORDER_ITEM_SUCCESS_ACTION({
        orderDetails: orderDetails,
        catentries: catentries,
        checkInventory: checkInventory,
      })
    );
  } catch (error) {
    yield put(FETCH_ACTIVE_INPROGRESS_ORDER_ITEM_ERROR_ACTION({ error }));
  }
}

export function* combineInProgressOrders(action: any) {
  try {
    // NO ACTION HERE
  } catch (error) {
    yield put({ type: COMBINE_INPROGRESS_ORDERS_ACTION.type, error });
  }
}
